import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { HTTP_STATUS } from 'src/app/@core/auth/constants/auth-constants';
import { AuthSessionService } from 'src/app/@core/auth/services/auth-session';
import { AuthTokenService } from 'src/app/@core/auth/services/auth-token';
import { NetworkingSessionService } from 'src/app/@services/networking-session.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { resolveMatchmakingEventIdFromState } from 'src/app/@utils/matchmaking/resolve-matchmaking-event-id';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { SessionService } from './session.service';

/** Login / OTP endpoints must NOT carry auth headers. */
const AUTH_FREE = [
  /\/auth\/login/i,
  /\/auth\/send-otp/i,
  /\/auth\/verify-otp/i,
  /\/auth\/resolve/i,
];

/** True when the request targets the show-winners API (same-origin `/api`, or an
 *  absolute `apiBase` origin in prod). Used to scope the show-winners auth headers
 *  so they are NEVER attached to the SEI app's `/r1` calls (which carry X-Api-Key
 *  via the existing SEI interceptor). An empty `apiBase` means same-origin, so we
 *  only match it when it is a non-empty configured origin. */
function isShowWinners(url: string): boolean {
  if (/^\/api(\/|$|\?)/i.test(url)) return true;
  const base = environment.matchmakingApiUrl;
  return !!base && url.startsWith(base);
}

function resolveEventId(
  session: SessionService,
  eventConfigState: EventConfigStateService
): string {
  return resolveMatchmakingEventIdFromState(
    eventConfigState,
    session.session()?.expoId
  );
}

function resolveUserId(session: SessionService): string | null {
  const rawId = session.userId();
  if (!rawId || rawId === 'preview') {
    return null;
  }
  return rawId;
}

function buildShowWinnersHeaders(
  userId: string | null,
  eventId: string | null,
  bearerToken: string | null
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  }
  if (userId) {
    headers['x-user-id'] = userId;
    headers['x-user-email'] = userId;
  }
  if (eventId) {
    headers['x-event-id'] = eventId;
  }
  return headers;
}

/**
 * Attaches show-winners auth to every networking backend request:
 *   • `Authorization: Bearer <idToken>` — Cognito (Amplify), when signed in
 *   • `x-user-id` / `x-user-email` — attendee email identity (legacy POC contract)
 *   • `x-event-id` — event scope
 * When Cognito is active, Bearer and x-user headers are sent together.
 * Email-dialog-only users (no Cognito) continue with x-user headers only.
 */
export const showWinnersAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const legacyAuth = inject(AuthService);
  const session = inject(SessionService);
  const eventConfigState = inject(EventConfigStateService);
  const authTokenService = inject(AuthTokenService);
  const authSessionService = inject(AuthSessionService);
  const networkingSessionService = inject(NetworkingSessionService);

  if (!isShowWinners(req.url)) {
    return next(req);
  }

  const skip = AUTH_FREE.some((re) => re.test(req.url));
  if (skip) {
    return next(req);
  }

  if (!session.userId()) {
    session.syncFromEnrollReport();
  }

  const eventId = resolveEventId(session, eventConfigState);

  const sendRequest = (
    headers: Record<string, string>,
    bearerToken: string | null
  ): Observable<HttpEvent<unknown>> => {
    const authReq = Object.keys(headers).length
      ? req.clone({ setHeaders: headers })
      : req;

    return next(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === HTTP_STATUS.UNAUTHORIZED && bearerToken) {
          return authTokenService.forceRefreshIdToken$().pipe(
            switchMap((newToken) => {
              const retryHeaders = buildShowWinnersHeaders(
                headers['x-user-id'] ?? null,
                headers['x-event-id'] ?? null,
                newToken
              );
              const retryReq = Object.keys(retryHeaders).length
                ? req.clone({ setHeaders: retryHeaders })
                : req;
              return next(retryReq);
            }),
            catchError(() => {
              legacyAuth.clear();
              return throwError(() => err);
            })
          );
        }

        return throwError(() => err);
      })
    );
  };

  const sendWithIdentity = (
    userId: string | null,
    bearerToken: string | null
  ): Observable<HttpEvent<unknown>> =>
    sendRequest(
      buildShowWinnersHeaders(userId, eventId, bearerToken),
      bearerToken
    );

  return authTokenService.getValidIdToken$().pipe(
    switchMap((amplifyIdToken) => {
      const bearerToken = amplifyIdToken || legacyAuth.getToken('id');
      let userId = resolveUserId(session);

      if (!userId && bearerToken) {
        const email =
          authTokenService.getIdTokenEmail() ??
          authSessionService.getCurrentAuthEmail();
        if (email) {
          networkingSessionService.syncShowWinnersIdentity(
            email,
            eventConfigState.$eventIdentifier() || undefined
          );
          userId = email.toLowerCase().trim();
        }
      }

      return sendWithIdentity(userId, bearerToken);
    }),
    catchError(() => {
      const userId = resolveUserId(session);
      return sendWithIdentity(userId, legacyAuth.getToken('id'));
    })
  );
};
